import { ResultMap, SearchParameters } from "./interfaces";

/**
 * Gestisce la mappa di Google Maps con marker, poligoni e clustering
 */
export class SearchMapManager {
    private polygon: any = null;
    private currentInfoWindow: any = null;
    private readonly provinceConTrattino: Set<string> = new Set([
        "Barletta-Andria-Trani",
        "Carbonia-Iglesias",
        "Forlì-Cesena",
        "Massa-Carrara",
        "Olbia-Tempio",
        "Verbano-Cusio-Ossola"
    ]);

    /**
     * Inizializza la mappa di Google Maps
     */
    public async initializeMapFromFetch(): Promise<void> {
        try {
            const baseUrl = this.getFullDomain();
            const fullUrl = `${baseUrl}/Search/MapResults`;

            const response = await fetch(fullUrl);
            if (!response.ok) {
                throw new Error('Errore nella risposta della fetch');
            }

            const parsedResults: ResultMap[] = await response.json();

            if (Array.isArray(parsedResults) && parsedResults.length > 0) {
                (window.google.maps.Polygon.prototype as any).getBounds = function () {
                    const bounds = new window.google.maps.LatLngBounds();
                    const paths = this.getPaths();

                    for (let i = 0; i < paths.getLength(); i++) {
                        const path = paths.getAt(i);
                        for (let ii = 0; ii < path.getLength(); ii++) {
                            bounds.extend(path.getAt(ii));
                        }
                    }
                    return bounds;
                };

                this.initializeMap(parsedResults);
            } else {
                this.initializeMap(parsedResults);
            }
        } catch (e) {
            console.error("Errore nel recupero dei dati:", e);
        }
    }

    // ====== METODI PRIVATI ======

    private initializeMap(resultsMap: ResultMap[]): void {
        const mapElement = document.getElementById("map");
        if (!mapElement) return;

        const map = new window.google.maps.Map(mapElement, {
            streetViewControl: false,
            fullscreenControl: false,
            minZoom: 6,
            disableDefaultUI: true,
            restriction: { latLngBounds: { north: 50, south: 35, west: 5, east: 20 } },
            mapId: '3085b997687f5fd0'
        });

        const buttonsControlDiv = document.createElement("div");
        buttonsControlDiv.id = "divCenterControlMapType";

        const toggleMapControl = this.createToggleMapTypes(map);
        buttonsControlDiv.appendChild(toggleMapControl);
        map.controls[window.google.maps.ControlPosition.TOP_LEFT].push(buttonsControlDiv);

        this.addPolygon(map, resultsMap);

        if (resultsMap && resultsMap.length > 0) {
            this.addMarkers(map, resultsMap);
        }
    }

    private createToggleMapTypes(map: any): HTMLButtonElement {
        const controlButtonRoadMap = document.createElement("button");
        controlButtonRoadMap.id = "btnToggleMapType";
        controlButtonRoadMap.className = "btn btn-primary material-symbols-outlined p-2 m-2";
        controlButtonRoadMap.textContent = "layers";
        controlButtonRoadMap.type = "button";

        let mapTypeId = "roadmap";

        controlButtonRoadMap.addEventListener("click", () => {
            if (mapTypeId === "roadmap") {
                map.setMapTypeId("hybrid");
                mapTypeId = "hybrid";
                controlButtonRoadMap.title = "Clicca per visualizzare la mappa stradale";
            } else {
                map.setMapTypeId("roadmap");
                mapTypeId = "roadmap";
                controlButtonRoadMap.title = "Clicca per visualizzare la mappa satellitare";
            }
        });

        return controlButtonRoadMap;
    }

    private async addPolygon(map: any, resultsMap: ResultMap[]): Promise<void> {
        if (this.polygon) {
            this.polygon.setMap(null);
            this.polygon = null;
        }

        const searchParameters: SearchParameters = {
            regione: (document.querySelector("input[name='SearchParameters.Regione']") as HTMLInputElement)?.value || '',
            provincia: (document.querySelector("input[name='SearchParameters.Provincia']") as HTMLInputElement)?.value || '',
            comune: (document.querySelector("input[name='SearchParameters.Comune']") as HTMLInputElement)?.value || '',
            latitudineNW: this.getCoordinateValue("input[name='SearchParameters.LatitudineNW']"),
            longitudineNW: this.getCoordinateValue("input[name='SearchParameters.LongitudineNW']"),
            latitudineSE: this.getCoordinateValue("input[name='SearchParameters.LatitudineSE']"),
            longitudineSE: this.getCoordinateValue("input[name='SearchParameters.LongitudineSE']")
        };

        if (this.hasRegionProvinceOrComune(searchParameters)) {
            await this.handleRegionProvinceComune(map, searchParameters, resultsMap);
        } else if (this.hasCoordinates(searchParameters)) {
            this.handleCoordinates(map, searchParameters);
        } else {
            this.handleResultsMap(map, resultsMap);
        }
    }

    private hasRegionProvinceOrComune(params: SearchParameters): boolean {
        return !!(params.regione || params.provincia || params.comune);
    }

    private hasCoordinates(params: SearchParameters): boolean {
        return params.latitudineNW !== null && params.longitudineNW !== null &&
               params.latitudineSE !== null && params.longitudineSE !== null;
    }

    private async handleRegionProvinceComune(map: any, searchParameters: SearchParameters, resultsMap: ResultMap[]): Promise<void> {
        try {
            const endpoint = this.getEndpoint(searchParameters);
            const response = await fetch(endpoint);
            if (!response.ok) {
                throw new Error(`Errore nella richiesta fetch: ${response.statusText}`);
            }

            const gadm = await response.json();
            const poligonDelimiter = this.getPoligonDelimiter(gadm, searchParameters);

            if (poligonDelimiter) {
                const paths = this.getPathsFromCoordinates(poligonDelimiter.coordinates, poligonDelimiter.type);
                const polygon = new window.google.maps.Polygon({
                    paths: paths,
                    strokeColor: '#01377B',
                    strokeOpacity: 0.3,
                    strokeWeight: 1,
                    fillColor: '#01377B',
                    fillOpacity: 0.17
                });

                polygon.setMap(map);
                const bounds = new window.google.maps.LatLngBounds();
                polygon.getPaths().forEach((path: any) => {
                    path.forEach((latlng: any) => bounds.extend(latlng));
                });
                map.fitBounds(bounds);
            } else if (resultsMap.length > 0) {
                this.handleResultsMap(map, resultsMap);
            } else {
                map.setCenter(new window.google.maps.LatLng(41.8719, 12.5674));
                map.setZoom(6);
            }
        } catch (error) {
            console.error("Errore durante l'aggiunta del poligono:", error);
        }
    }

    private handleCoordinates(map: any, params: SearchParameters): void {
        if (params.latitudineNW === null || params.longitudineNW === null ||
            params.latitudineSE === null || params.longitudineSE === null) return;

        const northWest = new window.google.maps.LatLng(params.latitudineNW, params.longitudineNW);
        const southEast = new window.google.maps.LatLng(params.latitudineSE, params.longitudineSE);

        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(northWest);
        bounds.extend(southEast);

        if (!bounds.isEmpty()) {
            map.fitBounds(bounds, 0);
        }
    }

    private handleResultsMap(map: any, resultsMap: ResultMap[]): void {
        if (resultsMap.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            let hasValidPosition = false;

            resultsMap.forEach(result => {
                if (result.Latitudine != null && result.Longitudine != null) {
                    const position = new window.google.maps.LatLng(result.Latitudine, result.Longitudine);
                    bounds.extend(position);
                    hasValidPosition = true;
                }
            });

            if (hasValidPosition) {
                map.fitBounds(bounds, 0);
            } else {
                map.setCenter(new window.google.maps.LatLng(42.5043, 12.5726));
                map.setZoom(6);
            }
        } else {
            map.setCenter(new window.google.maps.LatLng(42.5043, 12.5726));
            map.setZoom(6);
        }
    }

    private getEndpoint(params: SearchParameters): string {
        if (params.comune) {
            // ✅ URL-encode il nome del comune per gestire spazi e caratteri speciali
            const comuneEncoded = encodeURIComponent(params.comune.trim().toLowerCase());
            return `/googlemap/polygon/comune/${comuneEncoded}`;
        } else if (params.provincia) {
            const provinciaEncoded = encodeURIComponent(params.provincia.trim().toLowerCase());
            return `/googlemap/polygon/provincia/${provinciaEncoded}`;
        } else {
            const regioneEncoded = encodeURIComponent(params.regione.trim().toLowerCase());
            return `/googlemap/polygon/regione/${regioneEncoded}`;
        }
    }

    private getPoligonDelimiter(gadm: any, params: SearchParameters): any {
        if (!gadm || !gadm.features) return null;

        const comune = params.comune?.trim().toLowerCase();
        const provincia = params.provincia?.trim().toLowerCase();
        const regione = params.regione?.trim().toLowerCase();

        let objTmp;
        if (comune) {
            // ✅ Controlla che name esista prima di chiamare trim()
            objTmp = gadm.features.find((x: any) =>
                x.properties?.name?.trim().toLowerCase() === comune
            );
        } else if (provincia) {
            // ✅ Controlla che prov_name esista
            objTmp = gadm.features.find((x: any) =>
                x.properties?.prov_name?.trim().toLowerCase() === this.ripristinaNomeProvincia(provincia)
            );
        } else if (regione) {
            // ✅ Controlla che NAME_1 esista
            objTmp = gadm.features.find((x: any) =>
                x.properties?.NAME_1?.trim().toLowerCase() === regione
            );
        }

        return objTmp ? objTmp.geometry : null;
    }

    private getPathsFromCoordinates(coordinates: any[], type: string): any[] {
        if (type === "Polygon") {
            return coordinates.map(ring =>
                ring.map((coord: any) => new window.google.maps.LatLng(coord[1], coord[0]))
            );
        } else if (type === "MultiPolygon") {
            return coordinates.flatMap(polygon =>
                polygon.map((ring: any) =>
                    ring.map((coord: any) => new window.google.maps.LatLng(coord[1], coord[0]))
                )
            );
        }
        return [];
    }

    private addMarkers(map: any, resultsMap: ResultMap[]): void {
        if (!Array.isArray(resultsMap) || resultsMap.length === 0) return;

        const groups = this.groupByCoordinates(resultsMap);
        const markers: any[] = [];

        // Map per risalire dal marker al numero di beni che rappresenta
        const markerBeniCount = new Map<any, number>();

        groups.forEach(group => {
            const first = group[0];
            if (first.Latitudine == null || first.Longitudine == null) return;

            const isSingle = group.length === 1;
            const priceTag = isSingle
                ? this.createPriceTag(first.LottoId, first.VisibilitaPrezzo, first.Prezzo)
                : this.createStackedPriceTag(group);

            const marker = this.createMarker(map, first.Latitudine, first.Longitudine, priceTag);

            // Salva il numero di beni reali rappresentati da questo marker
            markerBeniCount.set(marker, group.length);

            marker.addListener('mouseenter', (event: any) => {
                event.target.className = isSingle ? 'price-tag price-tag-hover' : 'price-tag price-tag-stacked price-tag-hover';
            });
            marker.addListener('mouseleave', (event: any) => {
                event.target.className = isSingle ? 'price-tag' : 'price-tag price-tag-stacked';
            });
            marker.addListener('click', () => {
                if (isSingle) {
                    this.handleMarkerClick(map, marker, first);
                } else {
                    this.handleGroupMarkerClick(map, marker, group);
                }
            });

            markers.push(marker);
        });

        const interpolatedRenderer = {
            render: ({ count, position, markers: clusterMarkers }: any) => {
                // Somma i beni reali di tutti i marker nel cluster
                const totalBeni = clusterMarkers
                    ? (clusterMarkers as any[]).reduce((sum: number, m: any) => sum + (markerBeniCount.get(m) ?? 1), 0)
                    : count;

                const clusterTag = document.createElement("div");
                clusterTag.className = "cluster-tag";
                clusterTag.textContent = totalBeni.toString();

                const clusterElement = new window.google.maps.marker.AdvancedMarkerElement({
                    position,
                    content: clusterTag,
                    collisionBehavior: window.google.maps.CollisionBehavior.REQUIRED
                });

                clusterElement.addListener("click", () => {
                    if (!clusterMarkers || clusterMarkers.length === 0) return;

                    const bounds = new window.google.maps.LatLngBounds();
                    for (const m of clusterMarkers) {
                        if (m.position) bounds.extend(m.position);
                    }

                    const center = bounds.getCenter();
                    let targetZoom = map.getZoom() + 2;
                    if (targetZoom > 22) targetZoom = 22;

                    map.setCenter(center);
                    map.setZoom(targetZoom);
                });

                return clusterElement;
            }
        };

        const superClusterOptions = {
            radius: 60,   // ridotto da 200: evita di aggregare marker troppo distanti
            maxZoom: 22,
            minPoints: 3
        };

        new window.markerClusterer.MarkerClusterer({
            markers,
            map,
            renderer: interpolatedRenderer,
            algorithmOptions: superClusterOptions
        });
    }

    private applyJitterToGeoJsonResults(resultsMap: ResultMap[], jitterAmount: number = 0.00005): ResultMap[] {
        const seenCoords = new Map<string, number>();
        const jitteredResults: ResultMap[] = [];

        for (const result of resultsMap) {
            if (result.Latitudine != null && result.Longitudine != null) {
                const lat = result.Latitudine;
                const lng = result.Longitudine;
                const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;

                const count = seenCoords.get(key) || 0;

                let jitteredLat = lat;
                let jitteredLng = lng;

                if (count > 0) {
                    jitteredLat += (Math.random() - 0.5) * jitterAmount;
                    jitteredLng += (Math.random() - 0.5) * jitterAmount;
                }

                seenCoords.set(key, count + 1);

                jitteredResults.push({
                    ...result,
                    Latitudine: jitteredLat,
                    Longitudine: jitteredLng
                });
            } else {
                jitteredResults.push(result);
            }
        }

        return jitteredResults;
    }

    private createPriceTag(id: string, visibility: string, price: number | null): HTMLElement {
        const priceTag = document.createElement("div");
        priceTag.setAttribute("id", id);
        priceTag.className = "price-tag";
        priceTag.textContent = this.getFormattedPriceShort(visibility, price);
        return priceTag;
    }

    private createMarker(map: any, lat: number, lng: number, content: HTMLElement): any {
        return new window.google.maps.marker.AdvancedMarkerElement({
            map,
            position: { lat, lng },
            content,
            collisionBehavior: window.google.maps.CollisionBehavior.REQUIRED
        });
    }

    private handleMarkerClick(map: any, marker: any, result: ResultMap): void {
        if (this.currentInfoWindow) {
            this.currentInfoWindow.close();
            this.currentInfoWindow = null;
        }

        const infoWindow = new window.google.maps.InfoWindow({
            content: this.createInfoWindowContent(result)
        });
        infoWindow.open(map, marker);
        this.currentInfoWindow = infoWindow;

        infoWindow.addListener('closeclick', () => {
            this.currentInfoWindow = null;
        });
    }

    private createInfoWindowContent(result: ResultMap): string {
        const link_domain = this.getFullDomain();
        const photoUrl = result.UrlPhoto ? `${link_domain}/ImageThumb/${result.UrlPhoto}` : null;
        const sselUrl = `${link_domain}/${result.UrlScheda}`;
        const formattedPrice = this.getFormattedPriceExtended(result.VisibilitaPrezzo, result.Prezzo);
        const address = result.Indirizzo || "";

        if (photoUrl) {
            return `<div class="custom-infowindow">
                        <a href="${sselUrl}" class="d-block position-relative" target="_blank">
                            <img src="${photoUrl}" alt="Immagine">
                        </a>
                        <div class="info-content">
                            <span class="price text-primary">${formattedPrice}</span>
                            <span class="address">${address}</span>
                        </div>
                    </div>`;
        } else {
            return `<div class="custom-infowindow">
                        <a href="${sselUrl}" class="d-block position-relative" target="_blank">
                            <span class="material-symbols-outlined md-96 opacity-50 top-50 start-50 translate-middle position-absolute text-white">home_work</span>
                        </a>
                        <div class="info-content">
                            <span class="price text-primary">${formattedPrice}</span>
                            <span class="address">${address}</span>
                        </div>
                    </div>`;
        }
    }

    private getFormattedPriceShort(visibility: string, price: number | null): string {
        const visibilityOptions: Record<string, string> = {
            "Trattativa Riservata": "T.R.",
            "Importo": price != null ? "€ " + this.numberFormat(price) : "-"
        };
        return visibilityOptions[visibility] || "-";
    }

    private getFormattedPriceExtended(visibility: string, price: number | null): string {
        const visibilityOptions: Record<string, string> = {
            "Trattativa Riservata": "Trattativa Riservata",
            "Importo": price != null ? this.numberFormatFull(price) + " €" : "-"
        };
        return visibilityOptions[visibility] || "-";
    }

    private numberFormatFull(n: number): string {
        return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    private numberFormat(n: number): string {
        if (n < 10000) {
            return this.numberFormatFull(n);
        }
        const formatter = new Intl.NumberFormat('it-IT');
        let value = n / 1000;
        if (value < 1000) {
            return formatter.format(Math.round(value)) + 'k';
        }
        value = value / 1000;
        if (value < 100) {
            return formatter.format(parseFloat(value.toFixed(2))) + 'M';
        }
        return formatter.format(Math.round(value)) + 'M';
    }

    private ripristinaNomeProvincia(provincia: string): string {
        if (!provincia) return "";

        if (provincia.toLowerCase() === "bolzano/bozen") {
            return "Bolzano";
        }

        if (provincia.toLowerCase() === "valle d'aosta/vallée d'aoste") {
            return "Valle d'Aosta";
        }

        if (provincia.includes("-") && !this.provinceConTrattino.has(provincia)) {
            return provincia.replace(/-/g, " ");
        }

        return provincia;
    }

    private getCoordinateValue(selector: string): number | null {
        const element = document.querySelector(selector) as HTMLInputElement;
        const value = element?.value;
        return value ? parseFloat(value.replace(',', '.')) : null;
    }

    /**
     * Raggruppa i risultati per coordinata (arrotondata a 5 decimali).
     * Beni con coordinate identiche finiscono nello stesso gruppo.
     */
    private groupByCoordinates(resultsMap: ResultMap[]): ResultMap[][] {
        const map = new Map<string, ResultMap[]>();
        for (const result of resultsMap) {
            if (result.Latitudine == null || result.Longitudine == null) continue;
            const key = `${result.Latitudine.toFixed(5)},${result.Longitudine.toFixed(5)}`;
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(result);
        }
        return Array.from(map.values());
    }

    /** Marker per gruppi sovrapposti: mostra il contatore beni */
    private createStackedPriceTag(group: ResultMap[]): HTMLElement {
        const tag = document.createElement('div');
        tag.className = 'price-tag price-tag-stacked';
        tag.innerHTML = `<span class="price-tag-count">${group.length}</span>
            <span class="price-tag-label">beni</span>`;
        return tag;
    }

    /** Click su marker con più beni sovrapposti: apre InfoWindow con carousel */
    private handleGroupMarkerClick(map: any, marker: any, group: ResultMap[]): void {
        if (this.currentInfoWindow) {
            this.currentInfoWindow.close();
            this.currentInfoWindow = null;
        }

        let currentIndex = 0;

        const buildCarouselElement = (index: number): {
            container: HTMLElement;
            btnPrev: HTMLButtonElement;
            btnNext: HTMLButtonElement;
        } => {
            const result   = group[index];
            const domain   = this.getFullDomain();
            const photoUrl = result.UrlPhoto ? `${domain}/ImageThumb/${result.UrlPhoto}` : null;
            const sselUrl  = `${domain}/${result.UrlScheda}`;

            // Wrapper principale — usa la stessa classe del card singolo
            const container = document.createElement('div');
            container.className = 'custom-infowindow';

            // --- Blocco immagine con frecce overlay ---
            const imgWrap = document.createElement('div');
            imgWrap.style.cssText = 'position:relative;overflow:hidden;';

            const imgLink = document.createElement('a');
            imgLink.href   = sselUrl;
            imgLink.target = '_blank';
            imgLink.className = 'd-block position-relative';

            if (photoUrl) {
                const img = document.createElement('img');
                img.src = photoUrl;
                img.alt = 'Immagine';
                imgLink.appendChild(img);
            } else {
                const icon = document.createElement('span');
                icon.className   = 'material-symbols-outlined md-96 opacity-50 top-50 start-50 translate-middle position-absolute text-white';
                icon.textContent = 'home_work';
                imgLink.appendChild(icon);
            }
            imgWrap.appendChild(imgLink);

            const arrowBase = [
                'position:absolute;top:50%;transform:translateY(-50%);',
                'background:rgba(255,255,255,0.85);border:none;border-radius:50%;',
                'width:28px;height:28px;font-size:20px;line-height:1;',
                'cursor:pointer;color:#01377B;display:flex;align-items:center;',
                'justify-content:center;box-shadow:0 1px 4px rgba(0,0,0,.25);',
                'padding:0;z-index:1;'
            ].join('');

            // Freccia sinistra — sovrapposta all'immagine
            const btnPrev = document.createElement('button');
            btnPrev.type      = 'button';
            btnPrev.innerHTML = '&#8249;';
            btnPrev.style.cssText = arrowBase + 'left:6px;';
            imgWrap.appendChild(btnPrev);

            // Freccia destra — sovrapposta all'immagine
            const btnNext = document.createElement('button');
            btnNext.type      = 'button';
            btnNext.innerHTML = '&#8250;';
            btnNext.style.cssText = arrowBase + 'right:6px;';
            imgWrap.appendChild(btnNext);

            // Counter "1 / 3" piccolo in basso a destra sull'immagine
            const dot = document.createElement('span');
            dot.textContent   = `${index + 1} / ${group.length}`;
            dot.style.cssText = [
                'position:absolute;bottom:6px;right:8px;',
                'background:rgba(0,0,0,.45);color:#fff;',
                'font-size:11px;padding:1px 6px;border-radius:10px;',
                'pointer-events:none;'
            ].join('');
            imgWrap.appendChild(dot);

            container.appendChild(imgWrap);

            // --- Testo (stessa struttura del card singolo) ---
            const infoContent = document.createElement('div');
            infoContent.className = 'info-content';

            const priceEl = document.createElement('span');
            priceEl.className   = 'price text-primary';
            priceEl.textContent = this.getFormattedPriceExtended(result.VisibilitaPrezzo, result.Prezzo);

            const addrEl = document.createElement('span');
            addrEl.className   = 'address';
            addrEl.textContent = result.Indirizzo || '';

            const codEl = document.createElement('span');
            codEl.className = 'address';
            codEl.textContent = 'cod. ' + result.LottoId || '';

            infoContent.appendChild(priceEl);
            infoContent.appendChild(addrEl);
            infoContent.appendChild(codEl);
            container.appendChild(infoContent);

            return { container, btnPrev, btnNext };
        };

        // Prima apertura
        const first = buildCarouselElement(currentIndex);
        const infoWindow = new window.google.maps.InfoWindow({ content: first.container });

        const navigate = (direction: number) => {
            currentIndex = (currentIndex + direction + group.length) % group.length;
            const next = buildCarouselElement(currentIndex);
            next.btnPrev.addEventListener('click', (e) => { e.stopPropagation(); navigate(-1); });
            next.btnNext.addEventListener('click', (e) => { e.stopPropagation(); navigate(1); });
            infoWindow.setContent(next.container);
        };

        first.btnPrev.addEventListener('click', (e) => { e.stopPropagation(); navigate(-1); });
        first.btnNext.addEventListener('click', (e) => { e.stopPropagation(); navigate(1); });

        infoWindow.open(map, marker);
        this.currentInfoWindow = infoWindow;

        infoWindow.addListener('closeclick', () => {
            this.currentInfoWindow = null;
        });
    }

    /** Genera l'HTML del carousel per la InfoWindow dei gruppi */
    private createCarouselInfoWindowContent(group: ResultMap[], index: number, carouselId: string): string {
        const result = group[index];
        const domain = this.getFullDomain();
        const photoUrl = result.UrlPhoto ? `${domain}/ImageThumb/${result.UrlPhoto}` : null;
        const sselUrl = `${domain}/${result.UrlScheda}`;
        const formattedPrice = this.getFormattedPriceExtended(result.VisibilitaPrezzo, result.Prezzo);
        const address = result.Indirizzo || '';
        const counter = `${index + 1} / ${group.length}`;

        const imageHtml = photoUrl
            ? `<a href="${sselUrl}" class="d-block position-relative" target="_blank">
                   <img src="${photoUrl}" alt="Immagine" style="width:100%;display:block;">
               </a>`
            : `<a href="${sselUrl}" class="d-block position-relative bg-secondary d-flex align-items-center justify-content-center" target="_blank" style="height:120px;">
                   <span class="material-symbols-outlined opacity-50 text-white" style="font-size:48px;">home_work</span>
               </a>`;

        return `<div class="custom-infowindow" style="min-width:200px;overflow:visible;">
                    ${imageHtml}
                    <div class="info-content" style="padding:8px 10px 4px;">
                        <span class="price text-primary" style="font-weight:700;display:block;">${formattedPrice}</span>
                        <span class="address" style="font-size:12px;color:#555;">${address}</span>
                    </div>
                    <div style="display:flex;align-items:center;justify-content:space-between;padding:4px 8px;border-top:1px solid #eee;background:#fafafa;">
                        <button onclick="window['${carouselId}'](-1)" style="border:1px solid #01377B;background:#fff;border-radius:4px;padding:2px 8px;cursor:pointer;color:#01377B;font-size:16px;">&#8249;</button>
                        <span style="font-size:12px;color:#777;">${counter}</span>
                        <button onclick="window['${carouselId}'](1)" style="border:1px solid #01377B;background:#fff;border-radius:4px;padding:2px 8px;cursor:pointer;color:#01377B;font-size:16px;">&#8250;</button>
                    </div>
                </div>`;
    }

    private getFullDomain(): string {
        return window.location.protocol + "//" + window.location.host;
    }
}
